
import { globalApiErrorHandler } from '../../../../Tanso/components/Utils/utils';
import { 
  extractCountry, 
  isNonRenewableTechnology, 
  findCIForTechnology, 
  applyTDFactor, 
  safeParseFloat,
  extractCountriesFromSalesData,
  groupItemsByCountry,
  calculateWeightedAverage,
  initializeIntensityObject,
  createTechIntensityMap,
  getNonRenewableTechnologies
} from './CommonUtils';
import { isSEBusinessSegment, convertEnergyValue } from './TotalVolumes';

// Define constants for property keys and conversion factors
const BUSINESS_SEGMENT_PROPERTY = "Business Segment";
const REGION_GRID_PROPERTY = "Region | Grid";
const TECHNOLOGY_PROPERTY = "Technology";
const ASSET_PROPERTY = "Asset";
const OTHER_DETAILS_PROPERTY = "Other Details";
const LOCATION_PROPERTY = "Location";
const UNITS_PROPERTY = "Units";
const REGION_PROPERTY = "Region";
const R4Q_PROPERTY = "R4Q";
const GWH_TO_KWH = 1000000;
const TWH_TO_KWH = 1000000000;
const DEFAULT_GWH_UNITS = "GWh";
const DEFAULT_MWH_UNITS = "MWh";
const TWH_UNITS = "TWh";
const GWH_UNITS = "GWh";
const INTENSITY_FIELD = "2024 Power Plant Intensity (gCO2e/kWh)";

// Define interfaces for type safety
export interface DataItem {
  [REGION_GRID_PROPERTY]?: string;
  [BUSINESS_SEGMENT_PROPERTY]?: string;
  [TECHNOLOGY_PROPERTY]?: string;
  [ASSET_PROPERTY]?: string;
  [OTHER_DETAILS_PROPERTY]?: string;
  [LOCATION_PROPERTY]?: string;
  [UNITS_PROPERTY]?: string;
  [REGION_PROPERTY]?: string;
  [R4Q_PROPERTY]?: string | number;
  Country?: string;
  [key: string]: string | number | undefined;
}

export interface TechIntensityMap {
  [key: string]: number;
}

export interface GridBusinessSegmentMap {
  [gridRegion: string]: string | undefined;
}

export interface ProcessedNonRenewableItem {
  country: string;
  volumeInKwh: number;
  ci: number | null;
  rawCI: number | null;
  regionGrid?: string;
  technology?: string;
  otherDetails?: string;
  r4q: number;
  businessSegment: string;
  isSE: boolean;
  error: undefined;
}

export interface ProcessedShellGenItem {
  country: string;
  volumeInKwh: number;
  ci: number | null;
  rawCI: number | null;
  asset?: string;
  technology?: string;
  region?: string;
  r4q: number;
  otherDetails?: string;
  businessSegment: string;
  isSE: boolean;
  error: object | undefined | null;
}

export interface ErrorItem {
  error: boolean;
  message: string;
  country: string;
  volumeInKwh: number;
  ci: number;
  rawCI: number;
  regionGrid?: string;
}

export type ProcessedItem = ProcessedNonRenewableItem | ProcessedShellGenItem | ErrorItem;

export interface CalculationDetails {
  items: ProcessedItem[];
  units: string;
}

export interface NonRenewableIntensity {
  nonRenewableIntensity: number;
  isSE: boolean;
  calculationDetails: CalculationDetails;
}

export interface NonRenewableIntensities {
  [country: string]: NonRenewableIntensity;
}

/**
 * Calculate non-renewable intensities for power generation
 * @param shellGenPowerData - Shell generated power data
 * @param threePGenTechSpecificData - Third-party generation tech-specific data
 * @param techIntensitiesData - Technology intensities data
 * @param shellGeneratedIntensitiesData - Shell generated intensities data
 * @param total3PGenSalesData - Total third-party generation sales data
 * @param tdFactorsData - T&D factors data
 * @returns Non-renewable intensity data by country
 */
export function calculateNonRenewableIntensities(
  shellGenPowerData: DataItem[],
  threePGenTechSpecificData: DataItem[],
  techIntensitiesData: DataItem[],
  shellGeneratedIntensitiesData: DataItem[],
  total3PGenSalesData: DataItem[] = [],
  tdFactorsData: DataItem[] = []
): NonRenewableIntensities {

    // Extract countries from sales data
    const countries = extractCountriesFromSalesData(total3PGenSalesData);

const techIntensityMap = createTechIntensityMap(techIntensitiesData) ?? ({} as Record<string, number>);
// Create grid to business segment map for determining SE status
const gridBusinessSegmentMap = createGridBusinessSegmentMap(total3PGenSalesData);

// Extract non-renewable items from 3P generation data
const processedThreePGenData = extract3PNonRenewableItems(
  threePGenTechSpecificData,
  techIntensityMap,
  techIntensitiesData,
  tdFactorsData,
  gridBusinessSegmentMap
);

// Extract Shell's generation items
const processedShellGenData = extractShellGenItems(
  shellGenPowerData,
  techIntensityMap,
  techIntensitiesData,
  shellGeneratedIntensitiesData
);
    
    // Combine data sources
    const combinedData = [...processedShellGenData, ...processedThreePGenData];

    // Group by country
    const countryData = groupItemsByCountry(combinedData) as Record<string, ProcessedItem[]>;
    
    // Initialize non-renewable intensities
    const nonRenewableIntensities = initializeIntensityObject(countries, "nonRenewableIntensity") as NonRenewableIntensities;
    
    // Calculate weighted average by country
    Object.keys(countryData).forEach(country => {
      const countryItems = countryData[country];
      const weightedAverage = calculateWeightedAverage(countryItems);
      
      // Check if any item in this country is from SE business segment
      const isSE = countryItems.some(item => 'isSE' in item && item.isSE === true);
      
      nonRenewableIntensities[country] = {
        nonRenewableIntensity: weightedAverage,
        isSE,
        calculationDetails: {
          items: countryItems,
          units: isSE ? TWH_UNITS : GWH_UNITS
        }
      };
    });
    
    return nonRenewableIntensities;
    }


/**
 * Create a lookup map for business segments by grid region
 * @param total3PGenSalesData - Total 3P gen sales data
 * @returns Lookup map for business segments by grid region
 */
function createGridBusinessSegmentMap(
  total3PGenSalesData: DataItem[]
): GridBusinessSegmentMap {
  const gridBusinessSegmentMap: GridBusinessSegmentMap = {};
  
  if (!Array.isArray(total3PGenSalesData)) {
    return gridBusinessSegmentMap;
  }
  
  total3PGenSalesData.forEach(item => {
    const gridRegion = item[REGION_GRID_PROPERTY];
    if (!gridRegion) {
      return;
    }
    
    const businessSegment = item[BUSINESS_SEGMENT_PROPERTY];
    gridBusinessSegmentMap[gridRegion] = businessSegment;
  });
  
  return gridBusinessSegmentMap;
}


/**
 * Convert energy values to kWh based on business segment
 */
function convertToKWh(r4q: number, sourceUnits: string, isSE: boolean): { r4q: number; volumeInKwh: number } {
  if (isSE) {
    const convertedR4qTwh = convertEnergyValue(r4q, sourceUnits, TWH_UNITS);
    return { r4q: convertedR4qTwh, volumeInKwh: convertedR4qTwh * TWH_TO_KWH };
  } 
  
  const convertedR4q = convertEnergyValue(r4q, sourceUnits, GWH_UNITS);
  return { r4q: convertedR4q, volumeInKwh: convertedR4q * GWH_TO_KWH };
}

/**
 * Determine default CI based on technology
 */
function getDefaultCI(technology: string): number {
  const DEFAULT_GAS_CI = 400;
  const DEFAULT_COAL_CI = 820;
  const DEFAULT_NONRENEWABLE_CI = 500;
  
  const techLower = technology.toLowerCase();
  if (techLower.includes("gas")) {
    return DEFAULT_GAS_CI;
  } 
  if (techLower.includes("coal")) {
    return DEFAULT_COAL_CI;
  }
  return DEFAULT_NONRENEWABLE_CI;
}

/**
 * Apply T&D factor to raw CI with error handling
 */
function safeApplyTDFactor(rawCI: number, country: string, tdFactorsData: DataItem[]): number {
  if (!country) {
    return rawCI;
  }
  
  try {
    return applyTDFactor(rawCI, true, country, tdFactorsData) ?? rawCI;
  } catch (error) {
    globalApiErrorHandler("Error applying T&D factor", error)
    return rawCI;
  }
}

/**
 * Process non-renewable item from 3P generation data
 * @param item - 3P generation item
 * @param techIntensityMap - Technology intensity map
 * @param techIntensitiesData - Technology intensities data
 * @param tdFactorsData - T&D factors data
 * @param gridBusinessSegmentMap - Grid to business segment map
 * @returns Processed item with CI value
 */
export function process3PNonRenewableItem(
  item: DataItem, 
  techIntensityMap: object,
  techIntensitiesData: DataItem[], 
  tdFactorsData: DataItem[],
  gridBusinessSegmentMap: GridBusinessSegmentMap = {}
): ProcessedNonRenewableItem | ErrorItem {
  const DEFAULT_NONRENEWABLE_CI = 500;
  
  try {
    // Get basic item properties
    const regionGrid = item[REGION_GRID_PROPERTY] ?? '';
    const businessSegment = item[BUSINESS_SEGMENT_PROPERTY] ?? gridBusinessSegmentMap[regionGrid] ?? '';
    const isSE = isSEBusinessSegment(businessSegment);
    const sourceUnits = item[UNITS_PROPERTY] ?? DEFAULT_MWH_UNITS;
    const country = extractCountry(regionGrid);
    
    // Parse and convert energy values
    const initialR4q = safeParseFloat(String(item[R4Q_PROPERTY] ?? "")) ?? 0;
    const { r4q, volumeInKwh } = convertToKWh(initialR4q, sourceUnits, isSE);
    
    // Get carbon intensity
    let rawCI = findCIForTechnology(item[TECHNOLOGY_PROPERTY] ?? '', techIntensityMap, techIntensitiesData);
    
    // Use default if CI not found
    if (rawCI === null || rawCI === undefined || isNaN(Number(rawCI))) {
      rawCI = getDefaultCI(item[TECHNOLOGY_PROPERTY] ?? '');
    }
    
    // Apply T&D factor
    const ci = rawCI !== null && rawCI !== undefined 
      ? safeApplyTDFactor(rawCI, country, tdFactorsData) 
      : rawCI;
    
    // Create response object
    return {
      country,
      volumeInKwh: volumeInKwh ?? 0,
      ci,
      rawCI,
      regionGrid,
      ...(item[TECHNOLOGY_PROPERTY] ? { technology: item[TECHNOLOGY_PROPERTY] } : {}),
      ...(item[OTHER_DETAILS_PROPERTY] ? { otherDetails: item[OTHER_DETAILS_PROPERTY] } : {}),
      ...(typeof r4q !== 'undefined' ? { r4q } : {}),
      businessSegment,
      isSE,
    } as ProcessedNonRenewableItem;
  } catch (error) {
    // Return error object
    return {
      error: true,
      message: error instanceof Error ? error.message : "Unknown error",
      country: extractCountry(item?.[REGION_GRID_PROPERTY] ?? '') ?? 'unknown',
      volumeInKwh: 0,
      ci: DEFAULT_NONRENEWABLE_CI,
      rawCI: DEFAULT_NONRENEWABLE_CI
    };
  }
}

/**
 * Find matching asset intensity in Shell's generated intensities data
 * @param assetName - Asset name to look for
 * @param shellGeneratedIntensitiesData - Shell's intensities data
 * @returns Intensity value or null if not found
 */
export function findAssetIntensity(
  assetName: string | undefined, 
  shellGeneratedIntensitiesData: DataItem[]
): number | null {
  if (!Array.isArray(shellGeneratedIntensitiesData) || !assetName) {
    return null;
  }
  
  const assetMatch = shellGeneratedIntensitiesData.find(asset => asset[ASSET_PROPERTY] === assetName);
  
  if (!assetMatch) {
    return null;
  }
  
  const intensityStr = String(assetMatch[INTENSITY_FIELD] ?? "").replace(/\s+/g, '');
  
  if (intensityStr && intensityStr !== '-') {
    return safeParseFloat(intensityStr);
  }
  
  return null;
}

/**
 * Process Shell generation item
 * @param item - Shell generation item
 * @param techIntensityMap - Technology intensity map
 * @param techIntensitiesData - Technology intensities data
 * @param shellGeneratedIntensitiesData - Shell generated intensities data
 * @returns Processed item with CI value
 */
export function processShellGenItem(
  item: DataItem, 
  techIntensityMap: object, 
  techIntensitiesData: DataItem[], 
  shellGeneratedIntensitiesData: DataItem[]
): ProcessedShellGenItem {
  // Get business segment and determine if it's an SE segment
  const businessSegment = item[BUSINESS_SEGMENT_PROPERTY] ?? "";
  const isSE = isSEBusinessSegment(businessSegment);
  
  // Get source units
  const sourceUnits = item[UNITS_PROPERTY] ?? DEFAULT_GWH_UNITS;
  
  // Parse R4Q value and convert if needed
  let r4q = safeParseFloat(String(item[R4Q_PROPERTY] ?? ""));
  
  // Calculate volumeInKwh based on business segment
  let volumeInKwh: number;
  
  // Convert to appropriate units based on business segment
  if (isSE) {
    r4q = convertEnergyValue(r4q, sourceUnits, TWH_UNITS);
    // volumeInKwh is in kWh, so need to convert TWh to kWh (multiply by 1 billion)
    volumeInKwh = r4q * TWH_TO_KWH;
  } else {
    r4q = convertEnergyValue(r4q, sourceUnits, GWH_UNITS);
    // volumeInKwh is in kWh, so need to convert GWh to kWh (multiply by 1 million)
    volumeInKwh = r4q * GWH_TO_KWH;
  }
  
  // Try to find matching asset intensity
  let rawCI = findAssetIntensity(item[ASSET_PROPERTY], shellGeneratedIntensitiesData);
  
  // If no match found, use technology lookup as fallback
  if (rawCI === null && item[TECHNOLOGY_PROPERTY]) {
    rawCI = findCIForTechnology(item[TECHNOLOGY_PROPERTY], techIntensityMap, techIntensitiesData);
  }
  
  // No T&D factor for Shell's own generation
  const ci = rawCI;
  const country = item.Country ?? '';
  
  return {
    country,
    volumeInKwh,
    rawCI,
    ci,
    asset: item[ASSET_PROPERTY],
    technology: item[TECHNOLOGY_PROPERTY],
    region: item[REGION_PROPERTY] ?? "",
    r4q,
    otherDetails: item[LOCATION_PROPERTY] ?? "",
    businessSegment,
    isSE,
    error: null // Explicitly set `error` to `undefined` to match `ProcessedShellGenItem`
  };
}

/**
 * Extract non-renewable items from 3P generation data
 * @param threePGenTechSpecificData - 3P generation data
 * @param techIntensityMap - Technology intensity map
 * @param techIntensitiesData - Technology intensity data
 * @param tdFactorsData - T&D factors data
 * @param gridBusinessSegmentMap - Grid to business segment map
 * @returns Processed non-renewable items
 */
export function extract3PNonRenewableItems(
  threePGenTechSpecificData: DataItem[], 
  techIntensityMap: object,
  techIntensitiesData: DataItem[], 
  tdFactorsData: DataItem[],
  gridBusinessSegmentMap: GridBusinessSegmentMap = {}
): ProcessedItem[] {
  if (!Array.isArray(threePGenTechSpecificData)) {
    return [];
  }
  
  const processedItems: ProcessedItem[] = [];
  
  threePGenTechSpecificData
    .filter(item => item[TECHNOLOGY_PROPERTY] !== undefined && isNonRenewableTechnology(item[TECHNOLOGY_PROPERTY]))
    .forEach(item => {
      const processed = process3PNonRenewableItem(
        item, 
        techIntensityMap, 
        techIntensitiesData, 
        tdFactorsData,
        gridBusinessSegmentMap
      );
      
      if (processed.country && processed.ci !== null && processed.volumeInKwh > 0) {
        processedItems.push(processed);
      }
    });
  
  return processedItems;
}

/**
 * Extract Shell's generation items
 * @param shellGenPowerData - Shell generation data
 * @param techIntensityMap - Technology intensity map
 * @param techIntensitiesData - Technology intensity data
 * @param shellGeneratedIntensitiesData - Shell intensities data
 * @returns Processed Shell generation items
 */
export function extractShellGenItems(
  shellGenPowerData: DataItem[],
  techIntensityMap: object,
  techIntensitiesData: DataItem[],
  shellGeneratedIntensitiesData: DataItem[]
): ProcessedShellGenItem[] {
  if (!Array.isArray(shellGenPowerData)) {
    return [];
  }
  
  const processedItems: ProcessedShellGenItem[] = [];
  const nonRenewableTechnologies = getNonRenewableTechnologies();
  
  shellGenPowerData
    .filter(item => safeParseFloat(String(item[R4Q_PROPERTY] ?? '')) > 0)
    .forEach(item => {
      const processed = processShellGenItem(
        item,
        techIntensityMap,
        techIntensitiesData,
        shellGeneratedIntensitiesData
      );
      
      // Check if technology is non-renewable by examining the first part before pipe symbol
      const isNonRenewable = processed.technology && 
        nonRenewableTechnologies.some(nonRenewableTech => 
          processed.technology?.toLowerCase().includes(nonRenewableTech.toLowerCase())
        );
      
      if (processed.country && processed.ci !== null && processed.volumeInKwh > 0 && isNonRenewable) {
        processedItems.push(processed);
      }
    });
  
  return processedItems;
}